// The `supabase` singleton below started as project scaffolding, but this file
// is hand-maintained: edit it like any other source file. The generated
// Supabase artefact is `./types.ts`, which IS rewritten by `supabase gen types`
// and should not be edited by hand.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { generateSubmissionIdentifier } from '@/utils/magic-link/helpers';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from '@/config/eveBackend';
import { invokeFunction } from './invokeFunction';

const SUPABASE_PUBLISHABLE_KEY = SUPABASE_ANON_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true, // This is crucial for OTP links to work correctly
    storage: typeof window !== 'undefined' ? localStorage : undefined, // Use localStorage for session persistence across tabs
    flowType: 'pkce', // Use PKCE flow for more secure authentication
  },
  global: {
    headers: {
      'X-Client-Info': 'supabase-js/2.x'
    }
  },
  db: {
    schema: 'public'
  }
});


// Re-exported so the call sites that already import it from this module keep
// working; the implementation lives in ./invokeFunction.
export { invokeFunction };

// Add utility functions for magic links
export const sendMagicLink = async (email: string, redirectTo?: string, alternativeEmails?: string | string[]) => {
  try {
    // Generate a submission identifier
    const submissionIdentifier = generateSubmissionIdentifier();

    const finalRedirectTo = redirectTo || `${window.location.origin}/auth-callback?submission_identifier=${submissionIdentifier}`;

    // Ensure alternativeEmails is always an array or null
    let alternativeEmailsArray: string[] | null = null;

    if (alternativeEmails) {
      if (Array.isArray(alternativeEmails)) {
        alternativeEmailsArray = alternativeEmails.filter(email => email && typeof email === 'string');
      } else if (typeof alternativeEmails === 'string' && alternativeEmails.trim()) {
        alternativeEmailsArray = [alternativeEmails];
      }
    }

    // Determine which email to send the magic link to
    const destinationEmail = (alternativeEmailsArray && alternativeEmailsArray.length > 0)
      ? alternativeEmailsArray[0]
      : email;

    const { data, error } = await supabase.auth.signInWithOtp({
      email: destinationEmail,
      options: {
        emailRedirectTo: finalRedirectTo,
      }
    });

    if (error) throw error;

    // Persistence is best-effort: invokeFunction throws on non-503 errors,
    // so isolate it from the OTP success path.
    if (email) {
      try {
        const { data, error: sessionError } = await invokeFunction(supabase, 'update-magic-link-session', {
          body: {
            action: 'upsert',
            sessionData: {
              email,
              alternative_email: alternativeEmailsArray,
              submission_identifier: submissionIdentifier
            }
          }
        });

        if (sessionError || !data?.success) {
          console.warn('Non-critical error storing magic link session:', sessionError?.message || data?.error);
        }
      } catch (sessionPersistError) {
        console.warn('Non-critical error storing magic link session:', sessionPersistError);
      }
    }

    return {
      success: true,
      data,
      sentToAlternative: destinationEmail !== email
    };
  } catch (error) {
    console.error('Error sending magic link:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error sending magic link'
    };
  }
};
